import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {Field} from '@app/interfaces';
import {ConfirmDialogComponent, DialogType} from '../../../dialogs';
import {FormHelperService} from '@app/util/form-helper.service';
import { MatDialog } from '@angular/material/dialog';
import {Subject} from 'rxjs';
import {FormDataService} from '@app/services/data/form-data.service';
import {takeUntil} from "rxjs/operators";
import {sortByValue} from "@app/util/object.helper";

@Component({
    selector: 'app-form-input-repeater',
    templateUrl: './form-input-repeater.component.html',
    styleUrls: ['./form-input-repeater.component.scss'],
    standalone: false
})
export class FormInputRepeaterComponent implements OnInit, OnDestroy {

  @Input() control: FormArray;

  @Input() hideExcept: FormGroup = null;

  private destroy$ = new Subject<void>();

  public subFieldsOrder: string[];
  public field: Field<string>;
  public hasNoWorkOrderItems = false;

  constructor(private formHelperService: FormHelperService,
              private matDialog: MatDialog,
              private ref: ChangeDetectorRef,
              private formDataService: FormDataService) {
  }

  @Input('field') set setField(field: Field<string>) {
    this.field = field;

    let subFieldsOrder = [];
    if (this.field.config.subFieldsOrder) {
      subFieldsOrder = Object.entries(this.field.config.subFieldsOrder || {});
    } else {
      subFieldsOrder = Object.entries(this.field.config.subFields).map(([key], index) => [key, index]);
    }

    // sort array of pairs by second value
    subFieldsOrder.sort(sortByValue((pair => pair[1])));

    this.subFieldsOrder = subFieldsOrder.map(([key]) => key);

    // Check if this is a work order tasks repeater with no work order items
    this.hasNoWorkOrderItems = this.checkHasNoWorkOrderItems();
  }

  private checkHasNoWorkOrderItems(): boolean {
    if (!this.field?.config?.subFields) {
      return false;
    }

    // Check if any subfield uses work_order_items data source or has the task_name typeId
    for (const key in this.field.config.subFields) {
      const subField = this.field.config.subFields[key];

      if (subField?.config?.data_source === 'work_order_items'
        || subField?.config?.typeId === 'briefd.document.workflows.work_order.task_name') {
        // Check if choices are empty
        return !subField?.config?.choices || subField.config.choices.length === 0;
      }
    }

    return false;
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  ngOnInit() {
    this.formDataService.fieldValueUpdates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((updateValues) => {
        if (updateValues !== null && updateValues !== undefined && updateValues[this.field.config.typeId] !== undefined && updateValues[this.field.config.typeId].length > 0 && this.field.type === 'repeater' && this.field.config.typeId === 'briefd.client.accounts') {
          this.addRepeaterItem();

          let ibanSubFieldUuid = '';
          for (const key in this.field.config.subFields) {
            if (this.field.config.subFields[key].config.typeId === 'briefd.client.accounts_iban') {
              ibanSubFieldUuid = key;
            }
          }

          this.asFormGroup(this.asFormGroup(this.control.controls[0]).controls.value).controls[ibanSubFieldUuid].setValue(updateValues[this.field.config.typeId][0]);
        }
      });
  }

  addRepeaterItem() {
    this.control.push(this.formHelperService.prepareEmptyRepeaterItem(this.field, true));
    this.ref.markForCheck();
  }

  removeRepeaterItem(index) {
    const dialogRef = ConfirmDialogComponent.open(this.matDialog, DialogType.DELETE_REPEATER_ITEM, true);

    dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.control.removeAt(index);
          this.ref.markForCheck();
        }
      }
    );
  }

  asFormGroup(item: AbstractControl) {
    return item as FormGroup;
  }

  private rowSubFields = new WeakMap<AbstractControl, { [key: string]: Field<unknown> }>();

  // Each row gets its own deep copy of the sub-field configs. The shared
  // config object was mutated per-row by inline-field-edit (value /
  // currency conversion), which made one row's value bleed into the others.
  subFieldsForRow(wrappedItem: AbstractControl): { [key: string]: Field<unknown> } {
    let copy = this.rowSubFields.get(wrappedItem);
    if (!copy) {
      copy = structuredClone(this.field.config.subFields);
      this.rowSubFields.set(wrappedItem, copy);
    }
    return copy;
  }

  fieldsUpdated(event: Array<{ fieldUuid: string, value: number | string }> | Field, formGroup: FormGroup) {
    const values = formGroup.value;


    if ('value' in event) {
      values.value[event.fieldUuid] = event.value;
    } else {
      event.forEach((setField) => {
        // @ts-ignore
        values.value[setField.fieldUuid] = setField.value;
      });
    }


    formGroup.setValue(values);
  }
}
